import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Alert,
  Form,
  Pagination,
  Spinner,
  InputGroup,
  Badge,
} from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEllipsisV, 
  faTrash, 
  faExternalLinkAlt, 
  faPlus, 
  faSearch,
  faTimes,
  faImage
} from '@fortawesome/free-solid-svg-icons';
import { BaseUrl } from '../Routes/BaseUrl';
import {
  fetchOrders as fetchOrdersService,
  updateOrderStatus as updateOrderStatusService,
  deleteOrder as deleteOrderService,
  createOrder as createOrderService,
  findUserByMobile as findUserByMobileService,
  registerPatient as registerPatientService,
} from '../services/orderService';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showManageModal, setShowManageModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Create Order Form State
  const [createStep, setCreateStep] = useState(1); // 1: Find User, 2: Add Items, 3: Upload Prescription
  const [searchValue, setSearchValue] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [searchingUser, setSearchingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    fullName: '',
    gender: 'Male',
    age: ''
  });
  const [registering, setRegistering] = useState(false);
  const [items, setItems] = useState([{ name: '', quantity: 1, price: 0 }]);
  const [billingAddress, setBillingAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash on Delivery');
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [creating, setCreating] = useState(false);

  const navigate = useNavigate();

  const fetchOrders = async (pageToLoad = page) => {
    try {
      setError('');
      setLoading(true);
      const data = await fetchOrdersService({ page: pageToLoad, limit });
      setOrders(data?.items || []);
      setPage(data?.page || pageToLoad);
      setTotal(data?.total || 0);
    } catch (err) {
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleManageClick = (order) => {
    setSelectedOrder(order);
    setShowManageModal(true);
  };

  const handleCloseModal = () => {
    setShowManageModal(false);
    setSelectedOrder(null);
    setError('');
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder) return;
    
    try {
      setError('');
      setUpdatingStatus(true);
      const updated = await updateOrderStatusService(selectedOrder._id, newStatus);
      
      // Update the order in the list
      setOrders(prev =>
        prev.map(order =>
          order._id === selectedOrder._id
            ? { ...order, status: updated?.status || newStatus }
            : order
        )
      );
      
      // Update selected order
      setSelectedOrder(prev => ({ ...prev, status: updated?.status || newStatus }));
    } catch (err) {
      setError('Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOrder) return;
    
    try {
      setError('');
      setDeleting(true);
      await deleteOrderService(selectedOrder._id);
      
      // Remove order from list
      setOrders(prev => prev.filter(order => order._id !== selectedOrder._id));
      
      // Close modal
      handleCloseModal();
    } catch (err) {
      setError('Failed to delete order');
      setDeleting(false);
    }
  };

  const handleViewPrescription = () => {
    if (selectedOrder?.prescription) {
      window.open(selectedOrder.prescription, '_blank', 'noopener,noreferrer');
    }
  };

  // Create Order Functions
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setCreateStep(1);
    setError('');
    // Reset form
    setSearchValue('');
    setMobileNumber('');
    setSelectedUser(null);
    setShowRegistrationForm(false);
    setRegistrationData({ fullName: '', gender: 'Male', age: '' });
    setItems([{ name: '', quantity: 1, price: 0 }]);
    setBillingAddress('');
    setPaymentMethod('Cash on Delivery');
    setPrescriptionFile(null);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateStep(1);
    setError('');
    setShowRegistrationForm(false);
  };

  const handleFindUser = async () => {
    const trimmedSearch = searchValue.trim();
    const isEmailSearch = trimmedSearch.includes('@');

    if (!trimmedSearch) {
      setError('Please enter a valid mobile number or email');
      return;
    }

    if (!isEmailSearch) {
      if (trimmedSearch.length < 10) {
        setError('Please enter a valid 10-digit mobile number');
        return;
      }
      setMobileNumber(trimmedSearch);
    }

    try {
      setError('');
      setSearchingUser(true);
      setShowRegistrationForm(false);
      
      const user = await findUserByMobileService(
        isEmailSearch
          ? { email: trimmedSearch }
          : { mobileNumber: trimmedSearch }
      );
      
      if (!user) {
        setError('User not found');
        return;
      }
      
      setSelectedUser(user);
      setCreateStep(2);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('User not found');
        setShowRegistrationForm(false);
      } else {
        setError(err.response?.data?.message || 'Failed to find user');
      }
    } finally {
      setSearchingUser(false);
    }
  };

  const handleShowRegistrationForm = () => {
    setShowRegistrationForm(true);
    setError('');
  };

  const handleRegistrationChange = (field, value) => {
    setRegistrationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRegisterPatient = async () => {
    if (!registrationData.fullName) {
      setError('Please enter patient name');
      return;
    }

    try {
      setError('');
      setRegistering(true);

      const newPatient = await registerPatientService({
        fullName: registrationData.fullName,
        mobileNumber: mobileNumber,
        gender: registrationData.gender,
        age: registrationData.age ? parseInt(registrationData.age) : null
      });

      setSelectedUser(newPatient);
      setShowRegistrationForm(false);
      setCreateStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register patient');
    } finally {
      setRegistering(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, { name: '', quantity: 1, price: 0 }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    setItems(newItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  const handleCreateOrder = async () => {
    // Validate items
    const validItems = items.filter(item => item.name && item.quantity > 0 && item.price > 0);
    if (validItems.length === 0) {
      setError('Please add at least one valid item');
      return;
    }

    if (!billingAddress) {
      setError('Please enter billing address');
      return;
    }

    try {
      setError('');
      setCreating(true);

      const formData = new FormData();
      formData.append('userId', selectedUser._id);
      formData.append('items', JSON.stringify(validItems));
      formData.append('billingAddress', billingAddress);
      formData.append('address', billingAddress);
      formData.append('paymentMethod', paymentMethod);
      formData.append('handlingCharge', 0);
      formData.append('deliveryCharge', 0);
      formData.append('donation', 0);

      if (prescriptionFile) {
        formData.append('prescription', prescriptionFile);
      }

      const newOrder = await createOrderService(formData);

      // Add new order to the list
      setOrders(prev => [newOrder, ...prev]);
      setTotal(prev => prev + 1);

      // Close modal and reset
      handleCloseCreateModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setCreating(false);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  if (loading && !orders.length) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container fluid className="order-management-page">
      <Row>
        <Col xs={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Order Management</h4>
              <Button variant="primary" onClick={handleOpenCreateModal}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create New Order
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>User</th>
                    <th>Total Amount</th>
                    <th>Date</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">No orders found</td>
                    </tr>
                  ) : (
                    orders.map(order => (
                      <tr key={order._id}>
                        <td>{order.orderId || 'N/A'}</td>
                        <td>{order.user?.fullName || 'Guest User'}</td>
                        <td>₹{order.totalAmount?.toFixed(2) || order.billTotal?.toFixed(2) || '0.00'}</td>
                        <td>{new Date(order.date).toLocaleDateString()}</td>
                        <td className="text-end">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleManageClick(order)}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} className="me-2" />
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer className="px-3 border-0 d-lg-flex align-items-center justify-content-between mb-3">
              <Pagination className="mb-2 mb-lg-0">
                <Pagination.Prev
                  onClick={() => page > 1 && setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Pagination.Prev>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
                  const pageNumber = idx + 1;
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === page}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() => page < totalPages && setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next
                </Pagination.Next>
              </Pagination>
              <small className="fw-bold">
                Showing <b>{orders.length}</b> of <b>{total}</b> orders
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Unified Management Modal */}
      <Modal show={showManageModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {selectedOrder && (
            <>
              {/* Order Details Section */}
              <div className="mb-4">
                <h5 className="mb-3">Order Details</h5>
                <Row>
                  <Col md={4}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Order ID</small>
                      <strong>{selectedOrder.orderId || 'N/A'}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Date</small>
                      <strong>{new Date(selectedOrder.date).toLocaleDateString()}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Total Amount</small>
                      <strong className="text-success">
                        ₹{selectedOrder.totalAmount?.toFixed(2) || selectedOrder.billTotal?.toFixed(2) || '0.00'}
                      </strong>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Items List Section */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3">Order Items</h5>
                  <Table bordered size="sm">
                    <thead className="bg-light">
                      <tr>
                        <th>Item Name</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.name}</td>
                          <td className="text-center">{item.quantity} {item.measure || ''}</td>
                          <td className="text-end">₹{item.price?.toFixed(2)}</td>
                          <td className="text-end">₹{(item.quantity * item.price).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="fw-bold">
                        <td colSpan="3" className="text-end">Subtotal:</td>
                        <td className="text-end">
                          ₹{selectedOrder.items.reduce((sum, item) => sum + (item.quantity * item.price), 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}

              {/* User Info Section */}
              <div className="mb-4">
                <h5 className="mb-3">User Information</h5>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Name</small>
                      <strong>{selectedOrder.user?.fullName || 'Guest User'}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted d-block">Mobile Number</small>
                      <strong>{selectedOrder.user?.mobileNumber || 'N/A'}</strong>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Billing Address Section */}
              {selectedOrder.billingAddress && (
                <div className="mb-4">
                  <h5 className="mb-3">Billing Address</h5>
                  <p className="mb-0">{selectedOrder.billingAddress}</p>
                </div>
              )}

              {/* Status Control Section */}
              <div className="mb-4">
                <h5 className="mb-3">Status Management</h5>
                <Form.Group>
                  <Form.Label>Order Status</Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Select
                      value={selectedOrder.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={updatingStatus}
                      style={{ maxWidth: '300px' }}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </Form.Select>
                    {updatingStatus && (
                      <Spinner animation="border" size="sm" className="ms-3" />
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Changes are saved automatically
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Prescription Access Section */}
              {selectedOrder.prescription && (
                <div className="mb-4">
                  <h5 className="mb-3">Prescription</h5>
                  <div className="d-flex align-items-center">
                    <img 
                      src={selectedOrder.prescription} 
                      alt="Prescription thumbnail" 
                      style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer', borderRadius: '8px' }}
                      onClick={handleViewPrescription}
                      className="me-3"
                    />
                    <Button
                      variant="outline-info"
                      onClick={handleViewPrescription}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                      View Full Prescription
                    </Button>
                  </div>
                </div>
              )}

              {/* Destructive Action Section */}
              <div className="mt-4 pt-4 border-top">
                <h5 className="mb-3 text-danger">Danger Zone</h5>
                <p className="text-muted mb-3">
                  Deleting this order is permanent and cannot be undone.
                </p>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete Order
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Create Order Modal */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Order - Step {createStep} of 3</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          
          {/* Step 1: Find/Select User */}
          {createStep === 1 && (
            <div>
              <h5 className="mb-3">Step 1: Find User</h5>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number or Email</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Enter mobile number or email"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    disabled={showRegistrationForm}
                  />
                  <Button 
                    variant="primary" 
                    onClick={handleFindUser}
                    disabled={searchingUser || showRegistrationForm}
                  >
                    {searchingUser ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        Find User
                      </>
                    )}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Search for an existing user by mobile number or email
                </Form.Text>
              </Form.Group>

              {/* Show Register Button when user not found */}
              {error && !showRegistrationForm && !searchValue.includes('@') && (
                <div className="d-flex align-items-center justify-content-between">
                  <Button 
                    variant="success" 
                    onClick={handleShowRegistrationForm}
                    className="mt-2"
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Register New Patient
                  </Button>
                </div>
              )}
              {error && !showRegistrationForm && searchValue.includes('@') && (
                <Form.Text className="text-muted">
                  Registering a new patient requires a mobile number search.
                </Form.Text>
              )}

              {/* Registration Form */}
              {showRegistrationForm && (
                <Card className="mt-3 border-success">
                  <Card.Header className="bg-success text-white">
                    <h6 className="mb-0">Register New Patient</h6>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={mobileNumber}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter patient name"
                        value={registrationData.fullName}
                        onChange={(e) => handleRegistrationChange('fullName', e.target.value)}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Gender</Form.Label>
                          <Form.Select
                            value={registrationData.gender}
                            onChange={(e) => handleRegistrationChange('gender', e.target.value)}
                          >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Age</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Enter age"
                            min="0"
                            max="150"
                            value={registrationData.age}
                            onChange={(e) => handleRegistrationChange('age', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="secondary" 
                        onClick={() => {
                          setShowRegistrationForm(false);
                          setError('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="success" 
                        onClick={handleRegisterPatient}
                        disabled={registering}
                      >
                        {registering ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Registering...
                          </>
                        ) : (
                          'Register & Continue'
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Add Items */}
          {createStep === 2 && selectedUser && (
            <div>
              <h5 className="mb-3">Step 2: Add Items</h5>
              
              {/* Selected User Info */}
              <Alert variant="info" className="mb-3">
                <strong>Selected User:</strong> {selectedUser.fullName} ({selectedUser.mobileNumber})
              </Alert>

              {/* Items Form */}
              {items.map((item, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Item {index + 1}</h6>
                      {items.length > 1 && (
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      )}
                    </div>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Item Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Paracetamol 500mg"
                            value={item.name}
                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label>Quantity</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label>Price (₹)</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}

              <Button variant="outline-primary" size="sm" onClick={handleAddItem} className="mb-3">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Another Item
              </Button>

              {/* Total */}
              <div className="bg-light p-3 rounded mb-3">
                <h5 className="mb-0">Total: ₹{calculateTotal().toFixed(2)}</h5>
              </div>

              {/* Billing Address */}
              <Form.Group className="mb-3">
                <Form.Label>Billing Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter billing address"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                />
              </Form.Group>

              {/* Payment Method */}
              <Form.Group className="mb-3">
                <Form.Label>Payment Method</Form.Label>
                <Form.Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="Cash on Delivery">Cash on Delivery</option>
                  <option value="Online">Online</option>
                  <option value="Card">Card</option>
                </Form.Select>
              </Form.Group>

              <div className="d-flex justify-content-between">
                <Button variant="secondary" onClick={() => setCreateStep(1)}>
                  Back
                </Button>
                <Button variant="primary" onClick={() => setCreateStep(3)}>
                  Next: Upload Prescription
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Upload Prescription */}
          {createStep === 3 && (
            <div>
              <h5 className="mb-3">Step 3: Upload Prescription (Optional)</h5>
              
              <Form.Group className="mb-3">
                <Form.Label>Prescription Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPrescriptionFile(e.target.files[0])}
                />
                <Form.Text className="text-muted">
                  Upload a prescription image if required (optional)
                </Form.Text>
              </Form.Group>

              {prescriptionFile && (
                <Alert variant="success">
                  <FontAwesomeIcon icon={faImage} className="me-2" />
                  {prescriptionFile.name} selected
                </Alert>
              )}

              {/* Order Summary */}
              <div className="bg-light p-3 rounded mb-3">
                <h6>Order Summary</h6>
                <p className="mb-1"><strong>User:</strong> {selectedUser?.fullName}</p>
                <p className="mb-1"><strong>Items:</strong> {items.filter(i => i.name).length}</p>
                <p className="mb-1"><strong>Total:</strong> ₹{calculateTotal().toFixed(2)}</p>
                <p className="mb-0"><strong>Payment:</strong> {paymentMethod}</p>
              </div>

              <div className="d-flex justify-content-between">
                <Button variant="secondary" onClick={() => setCreateStep(2)}>
                  Back
                </Button>
                <Button 
                  variant="success" 
                  onClick={handleCreateOrder}
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating Order...
                    </>
                  ) : (
                    'Create Order'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default OrderManagement;